import React from 'react';
import { Link } from 'react-router-dom';
import { Users, CheckCircle, ArrowRight, X, Award, TrendingUp, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { mockServices, mockPodPackages } from '../mock';
import SEO from '../components/SEO';

const OffshorePods = () => {
  const service = mockServices.find(s => s.slug === 'offshore-pods');

  const botModel = [
    {
      phase: 'Build',
      description: 'We assemble a dedicated team of experts tailored to your specific requirements'
    },
    {
      phase: 'Operate',
      description: 'Our team integrates seamlessly with your operations, delivering results round-the-clock'
    },
    {
      phase: 'Transfer',
      description: 'When ready, we smoothly transition the team and knowledge to your organization'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="POD Teams — Dedicated Development Teams in India | NIVA Digital AI"
        description="Scale your engineering capacity with dedicated offshore POD teams. Save 40-60% on costs with highly skilled developers, 99.9% uptime, and seamless integration."
        keywords="offshore POD teams, dedicated development teams India, offshore developers, remote engineering teams, offshore software development"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/services/offshore-pods`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-cyan-50 via-teal-50 to-emerald-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')" }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/75 via-white/55 to-white/75" aria-hidden="true" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Users size={20} />
                <span>POD Teams</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Build Your Dream
                <span className="block text-red-600 mt-2">Team</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {service.shortDescription}
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                  Discuss Your Needs <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/5684450/pexels-photo-5684450.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Global team collaborating with a laptop and a globe in a modern office"
                  className="w-full h-full object-cover"
                  data-testid="pods-hero-image"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Build-Operate-Transfer Model */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Build - Operate - Transfer
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our proven engagement model for seamless team integration
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {botModel.map((phase, index) => (
              <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-xl">
                <CardHeader>
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-red-600">{index + 1}</span>
                  </div>
                  <CardTitle className="text-2xl">{phase.phase}</CardTitle>
                  <CardDescription className="text-base mt-2">{phase.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pod Packages Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Custom Tailored POD Teams
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Pre-assembled teams ready to integrate with your operations
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockPodPackages.map((pkg, index) => (
              <Card key={index} className={`border-2 transition-all duration-300 hover:shadow-2xl ${
                pkg.type === 'premium' ? 'border-red-600 shadow-xl scale-105' : 'hover:border-red-600'
              }`}>
                <CardHeader className={pkg.type === 'premium' ? 'bg-red-50' : ''}>
                  {pkg.type === 'premium' && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <CardTitle className="text-3xl text-center mb-2">{pkg.name}</CardTitle>
                  <CardDescription className="text-center text-base">{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  {pkg.coreTeam.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold mb-3 text-gray-900">Core Team</h4>
                      <ul className="space-y-2">
                        {pkg.coreTeam.map((member, idx) => (
                          <li key={idx} className="flex items-center text-sm">
                            {member.included ? (
                              <CheckCircle className="text-green-500 mr-2 flex-shrink-0" size={16} />
                            ) : (
                              <X className="text-gray-300 mr-2 flex-shrink-0" size={16} />
                            )}
                            <span className={member.included ? 'text-gray-700' : 'text-gray-400'}>
                              {member.role}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {pkg.specialists.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold mb-3 text-gray-900">Specialists</h4>
                      <ul className="space-y-2">
                        {pkg.specialists.map((specialist, idx) => (
                          <li key={idx} className="text-sm">
                            <p className="font-medium text-gray-700">{specialist.role}</p>
                            <p className="text-xs text-gray-500">{specialist.hours}</p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="border-t pt-6 mt-6">
                    <p className="text-2xl font-bold text-center mb-4 text-gray-900">{pkg.pricing}</p>
                    <Link to="/contact">
                      <Button className={`w-full ${
                        pkg.type === 'premium'
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white'
                      }`} variant={pkg.type === 'premium' ? 'default' : 'outline'}>
                        Get Started
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why PODs?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.benefits.map((benefit, index) => {
              const icons = [TrendingUp, Award, Users, DollarSign];
              const IconComponent = icons[index] || TrendingUp;
              return (
                <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 text-center">
                  <CardContent className="pt-6">
                    <IconComponent className="text-red-600 mx-auto mb-4" size={40} />
                    <p className="text-gray-700 font-medium">{benefit}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              POD Use Cases
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See how our dedicated POD teams transform business operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                    <Users className="text-blue-600" size={24} />
                  </div>
                  Healthcare Platform
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Senior living care platform needed to be rebuilt while scaling the engineering velocity while maintaining HIPAA compliance, with no clear AI adoption roadmap.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Deployed an AI-augmented PODs with engineers and specialists to rebuild their internal systems while integrating AI to improve cycle time and maintain observability 24/7.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: 99.98% uptime, 40% cost savings, HIPAA-compliant infrastructure maintained
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                    <TrendingUp className="text-green-600" size={24} />
                  </div>
                  E-commerce AI Transformation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Rapidly growing e-commerce company was losing margin to stockouts, over-ordering, and reactive customer support with no internal capability to adopt AI across their operations or engineering team.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>AI Enabled Solution:</strong> Deployment of AI adoption POD and AI-powered demand forecasting directly into the inventory management system along with embedding an AI-driven customer support layer, using real-time order and product data to resolve queries without human escalation.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: 28% reduction in stockouts, 3x faster customer query resolution, 32% drop in support escalations and delivered within a single quarter, with full internal capability transferred to the client team.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                    <Award className="text-purple-600" size={24} />
                  </div>
                  FinTech Compliance & Security
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> FinTech startup required security-focused infrastructure with SOC 2 compliance.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Security-focused POD with DevSecOps engineers, compliance specialists, and security auditors.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: SOC 2 Type II certified, zero security incidents, improved deployment velocity
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-red-600 transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-2xl mb-3 flex items-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                    <DollarSign className="text-orange-600" size={24} />
                  </div>
                  Multi-Cloud Migration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  <strong>Challenge:</strong> Enterprise needed to migrate legacy applications to multi-cloud environment.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>Solution:</strong> Migration-focused POD with cloud architects, migration specialists, and automation engineers.
                </p>
                <p className="text-gray-600 font-semibold text-red-600">
                  Result: 100+ applications migrated, zero downtime, 45% cost optimization achieved
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-red-500 to-orange-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Build Your Team Today
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Connect with us to discuss your team requirements and get started
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Schedule a Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OffshorePods;